<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div class="space-y-2">
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
          <BaseInput
            v-model="modifiedData.name"
            color="base"
            show-error-as-tooltip
            :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
        </div>
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
          <BaseInput
            v-model="modifiedData.code"
            color="base"
            show-error-as-tooltip
            :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
        </div>
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="companySite" class="text-sm font-medium">{{
            $t('routes.companySite')
          }}</label>
          <BaseSelectDropdown
            :items="dropdownCompanySites"
            :selected-item="selectedCompanySite"
            @update:selected="handleCompanySiteSelection"
            name="companySite"
            clearable
            show-error-as-tooltip
            :error="$v.companySiteId.required.$invalid ? $v.companySiteId.required.$message : ''" />
        </div>
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="address" class="text-sm font-medium">{{ $t('address') }}</label>
          <BaseInput v-model="modifiedData.address" color="base" />
        </div>
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="city" class="text-sm font-medium">{{ $t('city') }}</label>
          <BaseInput v-model="modifiedData.city" color="base" />
        </div>
        <BaseToggle
          :label="$t('warehouse.isLogistics')"
          name="isLogistics"
          v-model="modifiedData.isLogistics"
          light
          :disable="!modifiedData.isFromManager" />
        <BaseToggle
          :label="$t('baseObject.isActive')"
          name="isActive"
          v-model="modifiedData.isActive"
          light
          :disable="!modifiedData.isFromManager" />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { addOrEditWarehouse } from '@/services/api/materials';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { type Warehouse } from '@/types/Warehouse';
import { type SelectDropdownItem } from '@/types/Base';

import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    warehouse: Warehouse;
  }>(),
  {}
);

const modifiedData = ref<Warehouse>(deepCopy<Warehouse>(props.warehouse));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';
    validationModel.value.companySiteId = newData.companySiteId || '';

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.warehouse);
    isModified.value = valueChanged || $v.value.$invalid;
  },
  { deep: true }
);

const { dropdownCompanySites } = storeToRefs(useLookupsStore());
const { getCompanySitesLookup } = useLookupsStore();

onMounted(async () => {
  await getCompanySitesLookup();
  const companySiteValue = dropdownCompanySites.value.find(
    (item) => item.value == modifiedData.value.companySiteId
  );
  if (companySiteValue) selectedCompanySite.value = companySiteValue;
});

const selectedCompanySite = ref<SelectDropdownItem | null>(null);
function handleCompanySiteSelection(item: SelectDropdownItem) {
  selectedCompanySite.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.companySiteId = item ? item.value.toString() : '';
    modifiedData.value.companySite = item
      ? {
          id: item.value.toString(),
          name: item.name,
        }
      : undefined;
  }
}

function onDismissChanges() {
  modifiedData.value = deepCopy<Warehouse>(props.warehouse);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditWarehouse(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
  companySiteId: modifiedData.value.companySiteId || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  code: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  companySiteId: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
